import React from "react";
import { CheckCircle, Users, AlertCircle, Clock, FileText, Pause, AlertTriangle } from "lucide-react";
import { Badge } from "@shared/ui/badge";
import { DetailsSection } from "@features/cx/shared/components/DetailCard";

interface BulkReconnectionDetailCardProps {
  request: any;
  currentStatus: string;
}

export function BulkReconnectionDetailCard({
  request,
  currentStatus,
}: BulkReconnectionDetailCardProps) {
  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved": return "outline";
      case "rejected": return "destructive";
      case "pending": return "secondary";
      case "completed": return "default";
      case "submitted": return "secondary";
      case "in_progress": return "default";
      case "on hold": return "secondary";
      default: return "outline";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved": return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "rejected": return <AlertCircle className="h-4 w-4 text-red-500" />;
      case "pending": return <Clock className="h-4 w-4 text-amber-500" />;
      case "submitted": return <FileText className="h-4 w-4 text-blue-500" />;
      case "completed": return <CheckCircle className="h-4 w-4 text-green-700" />;
      case "in_progress": return <Clock className="h-4 w-4 text-blue-500" />;
      case "on_hold": return <Pause className="h-4 w-4 text-orange-500" />;
      default: return null;
    }
  };

  return (
    <div className="border rounded-lg bg-white shadow-sm overflow-hidden p-6 space-y-6">
      {/* Header & Stats Section */}
      <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
        <div className="flex items-center gap-4">
          <div className="bg-blue-50 text-blue-600 rounded-lg h-12 w-12 flex items-center justify-center">
            <Users className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 leading-none">
              Bulk Reconnection — {request.reason}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Batch ID: {request.batchId || request.requestNo}
            </p>
            <div className="flex gap-2 mt-3">
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-100 border-none px-3">
                Bulk
              </Badge>
              <Badge variant={getStatusBadgeVariant(currentStatus)} className="flex items-center gap-1">
                {getStatusIcon(currentStatus)}
                <span className="capitalize">{currentStatus.replace("_", " ")}</span>
              </Badge>
            </div>
          </div>
        </div>

        {/* Stats Summary Area - Matching Bulk Disconnect (4 Columns) */}
        <div className="grid grid-cols-4 gap-2 border rounded-lg p-3 bg-gray-50/50 min-w-[470px]">
          <div className="text-center px-2 border-r border-gray-200">
            <p className="text-[9px] uppercase font-bold text-gray-400 mb-1 leading-none">Total Consumers</p>
            <p className="text-xl font-bold text-blue-600">{request.stats?.total || "0"}</p>
          </div>
          <div className="text-center px-2 border-r border-gray-200">
            <p className="text-[10px] uppercase font-bold text-gray-400 mb-1 leading-none">Completed</p>
            <p className="text-xl font-bold text-green-600">{request.stats?.completed || "0"}</p>
          </div>
          <div className="text-center px-2 border-r border-gray-200">
            <p className="text-[10px] uppercase font-bold text-gray-400 mb-1 leading-none">Pending</p>
            <p className="text-xl font-bold text-orange-600">{request.stats?.pending || "0"}</p>
          </div>
          <div className="text-center px-2">
            <p className="text-[10px] uppercase font-bold text-gray-400 mb-1 leading-none">Created On</p>
            <p className="text-sm font-bold text-gray-900 leading-7">{request.stats?.createdOn || "NA"}</p>
          </div>
        </div>
      </div>

      {/* Batch Details Section */}
      <div className="-mx-1">
        <DetailsSection
          title="BATCH DETAILS"
          fields={[
            { label: "Request ID", value: request.batchId || request.requestNo },
            { label: "Reason", value: request.reason },
            { label: "Preferred Date", value: request.preferredDate },
            { label: "Time Slot", value: request.timeSlot },
          ]}
          columns={4}
        />
      </div>

      {/* Notes Section */}
      {request.notes && (
        <div className="space-y-2">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Notes</h3>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/30 text-sm italic text-gray-600">
            {request.notes}
          </div>
        </div>
      )}

      {/* Audit Info Section */}
      {request.auditInfo && (
        <div className="-mx-1 mt-6">
          <DetailsSection
            title="AUDIT INFO"
            fields={[
              { label: "Created By", value: request.auditInfo.createdBy },
              { label: "Created On", value: request.auditInfo.createdOn },
              { label: "Approved By", value: request.auditInfo.approvedBy },
              { label: "Approved On", value: request.auditInfo.approvedOn },
            ]}
            columns={4}
          />
        </div>
      )}
    </div>
  );
}
